'use client';

import React, { useEffect, useRef } from 'react';
import { usePerf } from '@/contexts/PerformanceContext';

const principles = [
  {
    index: '01',
    title: 'Know what deserves to be built.',
    body: 'We test every request, then build only what helps the user and the business.',
  },
  {
    index: '02',
    title: 'Make complexity feel simple.',
    body: 'Users finish tasks without learning the system. We hide the machinery.',
  },
  {
    index: '03',
    title: 'Build for what comes next.',
    body: "We choose architecture for the product you are becoming, not just today's.",
  },
] as const;

function PovSection() {
  const { reducedMotion } = usePerf();
  const motionOn = !reducedMotion;
  const sectionRef = useRef<HTMLElement>(null);
  const itemsRef = useRef<Array<HTMLLIElement | null>>([]);
  const emphasis = useRef([1, 0, 0]);

  useEffect(() => {
    if (!motionOn) return;

    let frame: number | null = null;
    let running = true;

    const write = (index: number, amount: number) => {
      const item = itemsRef.current[index];
      if (!item) return;
      item.style.opacity = (0.72 + amount * 0.28).toFixed(3);
      item.style.transform = `translate3d(${(amount * 3).toFixed(2)}px, 0, 0)`;
      const node = item.querySelector<HTMLElement>('[data-pov-node]');
      const numeral = item.querySelector<HTMLElement>('[data-pov-index]');
      if (node) {
        const on = amount > 0.62;
        node.style.backgroundColor = on ? '#2563FF' : '#FFFFFF';
        node.style.borderColor = on ? '#2563FF' : 'rgba(15, 17, 23, 0.22)';
      }
      if (numeral) {
        numeral.style.color = amount > 0.62 ? '#2563FF' : 'rgba(29, 33, 41, 0.45)';
      }
    };

    const step = () => {
      frame = null;
      const section = sectionRef.current;
      if (!section) return;

      const rect = section.getBoundingClientRect();
      const start = window.innerHeight * 0.72;
      const end = window.innerHeight * 0.28 - rect.height;
      const span = start - end || 1;
      const progress = Math.min(1, Math.max(0, (start - rect.top) / span));
      const active = Math.min(2, Math.floor(progress * 2.999));
      let moving = false;

      for (let index = 0; index < 3; index += 1) {
        const target = index === active ? 1 : 0;
        const next = emphasis.current[index] + (target - emphasis.current[index]) * 0.16;
        emphasis.current[index] = next;
        if (Math.abs(target - next) > 0.012) moving = true;
        write(index, next);
      }

      if (moving && running) frame = requestAnimationFrame(step);
    };

    const kick = () => {
      if (frame === null) frame = requestAnimationFrame(step);
    };

    kick();
    window.addEventListener('scroll', kick, { passive: true });
    window.addEventListener('resize', kick);

    const items = itemsRef.current;

    return () => {
      running = false;
      if (frame !== null) cancelAnimationFrame(frame);
      window.removeEventListener('scroll', kick);
      window.removeEventListener('resize', kick);
      items.forEach((item) => {
        if (!item) return;
        item.style.opacity = '';
        item.style.transform = '';
        const node = item.querySelector<HTMLElement>('[data-pov-node]');
        const numeral = item.querySelector<HTMLElement>('[data-pov-index]');
        if (node) {
          node.style.backgroundColor = '';
          node.style.borderColor = '';
        }
        if (numeral) numeral.style.color = '';
      });
    };
  }, [motionOn]);

  return (
    <section
      ref={sectionRef}
      id="pov"
      aria-labelledby="pov-title"
      className="scroll-mt-28 bg-white text-[#0F1117]"
    >
      <div className="mx-auto w-full max-w-[1440px] px-5 py-20 sm:px-8 lg:px-12 lg:pb-16 lg:pt-24 xl:px-16 xl:pb-20 xl:pt-28">
        <div className="mx-auto max-w-4xl text-center">
          <p className="text-[11px] font-medium uppercase tracking-[0.22em] text-[#2563FF]">
            Our POV
          </p>
          <h2
            id="pov-title"
            className="mx-auto mt-5 max-w-[12ch] font-semibold leading-[0.92] tracking-[-0.045em] text-[#0F1117] text-[clamp(2.15rem,8.4vw,2.85rem)] sm:text-[clamp(3rem,6vw,3.75rem)] lg:mt-6 lg:max-w-none lg:text-[clamp(3.35rem,5vw,4.15rem)] xl:text-[clamp(4.5rem,6vw,5.75rem)]"
          >
            <span className="text-[rgb(187,188,192)]">Code</span> is the cheaper part.
          </h2>
          <p className="mx-auto mt-6 max-w-md text-[15px] leading-relaxed text-[#1D2129]/75 sm:text-base lg:mt-8 lg:max-w-lg lg:text-[1.0625rem] xl:max-w-xl xl:text-lg">
            The difficult part is knowing what to build, why it matters, and how every decision
            connects to the product.
          </p>
        </div>

        <ol className="relative mx-auto mt-16 max-w-3xl lg:mt-20">
          <div
            className="absolute bottom-6 left-[9px] top-2 w-px bg-[#E5E7EB]"
            aria-hidden="true"
          />
          {principles.map((principle, index) => (
            <li
              key={principle.index}
              ref={(node) => {
                itemsRef.current[index] = node;
              }}
              className="grid grid-cols-[1.25rem_minmax(0,1fr)] gap-x-5 pb-14 last:pb-0 lg:pb-16 xl:gap-x-6 xl:pb-20"
            >
              <span
                data-pov-node
                className="relative z-10 mt-1 h-2.5 w-2.5 justify-self-center rounded-full border border-[#0F1117]/20 bg-white"
                aria-hidden="true"
              />
              <div className="min-w-0">
                <p
                  data-pov-index
                  className="text-[11px] font-medium tracking-[0.2em] text-[#2563FF]"
                >
                  {principle.index}
                </p>
                <h3 className="mt-3 text-[1.35rem] font-medium leading-snug tracking-[-0.03em] text-[#0F1117] sm:text-2xl lg:text-[1.75rem] xl:text-[2rem]">
                  {principle.title}
                </h3>
                <p className="mt-3 max-w-sm text-[15px] leading-relaxed text-[#1D2129]/75 lg:max-w-none lg:text-base xl:text-lg">
                  {principle.body}
                </p>
              </div>
            </li>
          ))}
        </ol>
      </div>
    </section>
  );
}

export default PovSection;
